import React, { useState, useEffect } from 'react';
import './courses.css'
import C2wCourseCard from '../../../common/C2wHome/courses/courseCard';
import { useNavigate } from 'react-router';
import { Helmet } from 'react-helmet';
// import coursesData from '/data/coursesData.json'

function C2wCourses() {

    const navigate = useNavigate()

    /**
 * @state windowWidth - Tracks the current width of the browser window.
 * @hook useEffect - Window Resize Listener
 * @author Vishal Lohbande
 * Description:
 * - Initializes `windowWidth` state with the current width of the browser window.
 * - Adds an event listener to update the state whenever the window is resized.
 *
 * Behavior:
 * - `handleResize` updates the `windowWidth` state using `window.innerWidth`.
 * - The resize listener is attached once on component mount.
 * - On unmount, the event listener is properly cleaned up to prevent memory leaks.
 *
 * Use Case:
 * - Responsive UI adjustments (e.g., conditional rendering based on screen width).
 * - Dynamic layout shifts or breakpoints detection in React components.
 *
 * Notes:
 * - This setup ensures that UI can respond in real-time to screen size changes.
 */
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**
     * @state coursesData
     * @function setCourseData
     * @description
     * - `coursesData` holds the array or object of all course-related information fetched from a static JSON file.
     * - Initialized as `null` and updated after successful data fetch.
     */
    const [coursesData, setCourseData] = useState(null);


    /**
 * @hook useEffect
 * @author Vishal Lohbande
 * @Date 28-05-2025
 *
 * useEffect - Fetches general course data from a static JSON file on component mount.
 *
 * Behavior:
 * - Runs only once when the component mounts (due to empty dependency array `[]`).
 * - Sends a GET request to `/data/coursesData.json`.
 * - Parses the response as JSON.
 * - Sets the parsed data to `coursesData` using `setCourseData`.
 * - Logs an error message in the console if the fetch operation fails.
 *
 * Usage Scenario:
 * - Typically used to load and render a list of available courses on the homepage or course catalog.
 * - Ensures the application has access to course data as soon as it loads.
 *
 * Notes:
 * - The JSON file should be located in the `public/data/` directory to be accessible via `/data/coursesData.json`.
 * - Error handling helps in debugging and displaying fallback UI if needed.
 */
    useEffect(() => {
        fetch("/data/coursesData.json")
            .then((res) => res.json())               // parse JSON
            .then((data) => {
                setCourseData(data);                   // set the parsed data
            })
            .catch((err) => {
                console.error("Failed to fetch course details:", err);
            });
    }, []);

    /**
 * @constant renderdCoursesCards
 * @author Vishal Lohbande
 *
 * Description:
 * - Maps over the first 4 items of the `courses` array inside `coursesData`.
 * - Generates an array of `C2wCourseCard` components, each receiving a course object as prop.
 *
 * Behavior:
 * - Uses optional chaining (`?.`) to safely access nested data.
 * - Limits the rendered courses to a maximum of 4 using `slice(0, 4)`.
 *
 * Usage:
 * - Renders a preview or highlight section for courses on the UI.
 *
 * Notes:
 * - Consider adding a unique `key` prop (e.g., `key={index}` or unique course ID) for list rendering performance.
 */
    const renderdCoursesCards = coursesData?.courses?.slice(0, 4).map((obj, index) => {
        return <C2wCourseCard obj={obj} />
    })

    return (
        windowWidth > 620 ?
            <div className='C2wCourses_ctn'>
                <Helmet>
                    {/* 
        Purpose: Defines the page title displayed in search results and browser tabs. 
                 It’s one of the most important SEO elements. 
        */}
                    <title>{`Courses`}</title>

                    {/* 
        Purpose: Provides a brief summary of the page content in search results. 
                 While not a direct ranking factor, it influences CTR. 
        */}
                    <meta name="description" content="High-quality Course with best Content." />

                    {/* Purpose: Controls indexing and link-following behavior. */}
                    <meta name="robots" content="index, follow" />

                </Helmet>
                <div className='C2wCoursesHeading_ctn_hld'>
                    <div className='C2wCoursessHeading_ctn'>
                        {'Explore our interactive '}
                        <span className='C2wCorsessHeadingSpan'>{'Courses'}</span>

                    </div>
                    <div className='C2wCourses_ctn_subheading'>{' Beginner friendly | From Scratch to advanced | Value for money | Learn from the best in industry'}</div>
                </div>
                <div className='C2wCorses_card_ctn'>
                    {renderdCoursesCards}
                </div>
                <button className="C2wCoursesExploreBtn" onClick={() => {
                    navigate('/courses')
                }}>
                    <span className="buttonText">All Courses</span>

                    {/* Sliding Background with Hover Arrow */}
                    <div className="AnimatedButton">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M9 12H23"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M16 5L23 12L16 19"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>

                    {/* Default Arrow */}
                    <svg
                        className="defaultArrow"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M9 12H23"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M16 5L23 12L16 19"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

            </div> :
            <div className='C2wCourses_ctn'>
                <Helmet>
        {/* 
        Purpose: Defines the page title displayed in search results and browser tabs. 
                 It’s one of the most important SEO elements. 
        */}
        <title>{`Courses`}</title>  

        {/* 
        Purpose: Provides a brief summary of the page content in search results. 
                 While not a direct ranking factor, it influences CTR. 
        */}
        <meta name="description" content="High-quality Course with best Content." />
        
        {/* Purpose: Controls indexing and link-following behavior. */}
        <meta name="robots" content="index, follow" />
        
      </Helmet>
                <div className='C2wCoursesHeading_ctn_hld'>
                    <div className='C2wCoursessHeading_ctn'>
                        {'Explore Popular '}
                        <span className='C2wCorsessHeadingSpan'>{'Courses'}</span>
                    </div>
                    <div className='C2wCourses_ctn_subheading'>{'Beginner friendly | From Scratch to advanced | Value for money | Learn from the best in industry'}</div>
                </div>
                <div className='C2wCorses_card_ctn'>
                    {renderdCoursesCards}
                </div>
                <button className="C2wCoursesExploreBtn coursesBtn" onClick={() => {
                    navigate('/courses')
                }}>
                    <span className="buttonText">All Courses</span>

                    {/* Sliding Background with Hover Arrow */}
                    <div className="AnimatedButton">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M9 12H23"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M16 5L23 12L16 19"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>

                    {/* Default Arrow */}
                    <svg
                        className="defaultArrow"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M9 12H23"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M16 5L23 12L16 19"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

            </div>
    )
}

export default C2wCourses;
